'use client'

import { Suspense, useState } from 'react'

import { useRouter } from 'next/navigation'

import _has from 'lodash/has'
import _isEmpty from 'lodash/isEmpty'
import { LogOut, Search } from 'lucide-react'

import { getSubscriptionInfo } from '@/components/modules/user/services/billing'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CenteredLoader } from '@/components/ui/loading'
import { ErrorMessage } from '@/components/ui/message'
import useAsyncEffect from '@/hooks/use-async-effect'
import useAsyncOnUpdate from '@/hooks/use-async-on-update'
import useDebounce from '@/hooks/use-debounce'
import { SubscriptionItem, useGlammaticApi } from '@/hooks/use-glammatic-api'
import useStorage from '@/hooks/use-storage'
import { clearBusinessStorage } from '@/hooks/use-storage/business'
import {
  BUSINESS_STORAGE_KEY,
  SUBSCRIPTION_REFRESH_EXPIRY_DAYS,
  SUBSCRIPTION_REFRESHED_STORAGE_KEY,
  SUBSCRIPTION_STORAGE_KEY,
  USER_AUTH_STORAGE_KEY,
} from '@/hooks/use-storage/use-storage.constants'
import useUserSessionGuard from '@/hooks/use-user-session-guard'
import DateUtils from '@/lib/date/date'
import { multiCasePick } from '@/lib/json/case'
import { serializeObject } from '@/lib/json/serialization'
import { isAdminUser } from '@/lib/user/auth'
import { createOrUpdateMerchant } from '@/server/actions/merchant/create'
import { getMerchantSubscriptionInfo } from '@/server/actions/merchant/get'
import { updateUserBusinesses } from '@/server/actions/user/create'

import { DASHBOARD_HOME_SLUG, LOGIN_SLUG } from '../constants'

import { BusinessCard } from './components/BusinessCard'
import { LOGOUT_FALLBACK_REDIRECT_MS } from './page.constants'
import { areAllDuplicates, getLiveBusinesses, sortBusinesses } from './page.utils'

function BusinessSelectionPageContent() {
  const router = useRouter()

  const { value: storageUser, storageLoaded: storageUserLoaded } =
    useStorage<UserAuth>(USER_AUTH_STORAGE_KEY)
  const isAdmin = isAdminUser(storageUser)

  const {
    value: storageBusiness,
    setValue: setStorageBusiness,
    storageLoaded: storageBusinessLoaded,
  } = useStorage<Business>(BUSINESS_STORAGE_KEY)

  const {
    value: subscription,
    setValue: setSubscription,
    storageLoaded: storageSubscriptionLoaded,
  } = useStorage<SubscriptionItem[]>(SUBSCRIPTION_STORAGE_KEY)

  const {
    value: subscriptionRecentlyRefreshed,
    setValue: setSubscriptionRecentlyRefreshed,
    storageLoaded: storageSubscriptionRefreshedLoaded,
  } = useStorage<boolean>(SUBSCRIPTION_REFRESHED_STORAGE_KEY)

  const storageLoaded =
    storageUserLoaded &&
    storageBusinessLoaded &&
    storageSubscriptionLoaded &&
    storageSubscriptionRefreshedLoaded

  const {
    logout,
    getBusinesses,
    getWebsiteDetail,
    scheduleTasksCreationCronJob,
    loaded: apiLoaded,
  } = useGlammaticApi()

  const [loading, setLoading] = useState(true)
  const [initialLoaded, setInitialLoaded] = useState(false)

  const [searchLoading, setSearchLoading] = useState(false)

  const [initialBusinesses, setInitialBusinesses] = useState<Business[]>([])
  const [businesses, setBusinesses] = useState<Business[]>([])
  const [subscriptionByBusiness, setSubscriptionByBusiness] = useState<
    Record<number, boolean>
  >({})

  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery)

  const [noMerchantAccess, setNoMerchantAccess] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)

  async function fetchBusinesses({ initial = false } = {}) {
    const response = await getBusinesses({ search: debouncedSearchQuery })
    const { results = [] } = response

    const sortedResults = sortBusinesses(results)

    if (initial && !_isEmpty(sortedResults)) {
      if (!isAdmin) {
        const userBusinesses = sortedResults.map(business => {
          return multiCasePick(business, [
            'id',
            'mid',
            'name',
            'type',
            'website.id',
            'website.url',
            'category',
            'businessInfoId',
            'brandIdentity',
          ]) as Business
        })

        await updateUserBusinesses(userBusinesses)
      }
    }

    if (initial) {
      setNoMerchantAccess(sortedResults.length === 0)
    }

    if (!isAdmin) {
      if (initial && sortedResults.length === 1) {
        await onBusinessSelect(sortedResults[0])
      }
      if (initial && sortedResults.length > 1) {
        const liveBusinesses = getLiveBusinesses(sortedResults)
        if (liveBusinesses.length === 1 && areAllDuplicates(sortedResults)) {
          await onBusinessSelect(liveBusinesses[0])
        }
      }
    }

    setBusinesses(sortedResults)
    if (initial) {
      setInitialBusinesses(sortedResults)
      setInitialLoaded(true)
    }
  }

  useAsyncEffect(async () => {
    if (storageLoaded && apiLoaded) {
      if (!_isEmpty(storageBusiness)) {
        await onBusinessSelect(storageBusiness)
        return
      }
      try {
        setLoading(true)
        await fetchBusinesses({ initial: true })
      } finally {
        setLoading(false)
      }
    }
  }, [storageLoaded, apiLoaded])

  useAsyncOnUpdate(async () => {
    try {
      if (!initialLoaded) {
        return
      }
      if (_isEmpty(debouncedSearchQuery)) {
        setBusinesses(initialBusinesses)
        setSearchLoading(false)
        return
      }

      setSearchLoading(true)
      await fetchBusinesses()
    } finally {
      setSearchLoading(false)
    }
  }, [debouncedSearchQuery, initialLoaded])

  useAsyncEffect(async () => {
    if (!debouncedSearchQuery || _isEmpty(businesses)) {
      return
    }

    if (!areAllDuplicates(businesses) && businesses.length !== 1) {
      return
    }

    const businessesToFetch = businesses.filter(
      business => !_has(subscriptionByBusiness, business.id)
    )
    if (businessesToFetch.length === 0) {
      return
    }

    const newSubscriptionByBusiness = Object.fromEntries(
      await Promise.all(
        businessesToFetch.map(async business => {
          const subscriptionInfo = await getMerchantSubscriptionInfo(business.id)

          return [business.id, subscriptionInfo?.hasAnySubscription || false]
        })
      )
    )

    setSubscriptionByBusiness(prev => ({
      ...prev,
      ...(newSubscriptionByBusiness || {}),
    }))
  }, [debouncedSearchQuery, businesses])

  async function loadSubscription(business: Business) {
    const { website } = business || {}
    const { id: websiteId } = website || {}

    const websiteDetails = await getWebsiteDetail({ websiteId })
    const { subscriptionData } = websiteDetails || {}
    const { subscriptionItems = [] } = subscriptionData || {}
    setSubscription(subscriptionItems)
    setSubscriptionRecentlyRefreshed(true, {
      expiry: DateUtils.addDays(new Date(), SUBSCRIPTION_REFRESH_EXPIRY_DAYS),
    })

    const subscriptionInfo = getSubscriptionInfo(websiteDetails)
    await createOrUpdateMerchant(
      serializeObject({ ...business, subscriptionInfo, websiteId })
    )
  }

  async function onLogout() {
    setLoggingOut(true)
    const currentUrl = window.location.href
    await logout()
    setTimeout(() => {
      if (window.location.href === currentUrl) {
        router.replace(LOGIN_SLUG)
      }
    }, LOGOUT_FALLBACK_REDIRECT_MS)
  }

  async function onBusinessSelect(business: Business) {
    setLoading(true)

    const isDifferentBusiness = storageBusiness?.id !== business.id

    if (isDifferentBusiness) {
      clearBusinessStorage()
    }

    if (_isEmpty(subscription) || isDifferentBusiness) {
      await loadSubscription(business)
    } else if (!subscriptionRecentlyRefreshed) {
      void loadSubscription(business)
    }
    await scheduleTasksCreationCronJob({ businessId: business.id, useCacheResult: true })

    setStorageBusiness(business)

    router.push(DASHBOARD_HOME_SLUG)
  }

  const pageLoading = loading || !storageLoaded || !initialLoaded
  const noBusinesses = _isEmpty(businesses)
  const showSearch = initialLoaded && !noMerchantAccess

  return (
    <div className="min-h-screen bg-background">
      {/* Header - Fixed at top */}
      <header className="fixed top-0 z-50 w-full bg-background border-b safe-area-top">
        <div className="py-6">
          <div className="max-w-2xl mx-auto px-4">
            <h1 className="text-2xl font-bold text-header-foreground mb-4 text-center">
              Select Business
            </h1>

            {showSearch && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-placeholder" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 !text-base"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                />
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Business List - Scrollable content with top padding to account for fixed header */}
      <main className="pt-32 bg-background">
        <div className="business-list-container max-w-2xl mx-auto px-4 py-4">
          <div className="space-y-3">
            {pageLoading && (
              <CenteredLoader
                message="Loading your business..."
                minHeight="min-h-[calc(100vh-8rem)]"
              />
            )}

            {!pageLoading && noMerchantAccess && (
              <ErrorMessage
                title="No business linked to this account"
                message="This login isn't associated with a business. Contact support to get access."
              />
            )}

            {!pageLoading && searchLoading && (
              <CenteredLoader
                minHeight="min-h-[calc(20vh)]"
                message="Searching for businesses..."
              />
            )}

            {!pageLoading && noBusinesses && !searchLoading && !noMerchantAccess && (
              <div className="text-center pt-12 pb-2">
                <div className="text-placeholder font-medium">
                  No businesses found matching your search
                </div>
              </div>
            )}

            {!pageLoading &&
              !searchLoading &&
              businesses.map(business => (
                <BusinessCard
                  key={business.id}
                  business={business}
                  onClick={onBusinessSelect}
                  showMetadata={isAdmin}
                  activeSubscription={subscriptionByBusiness[business.id]}
                />
              ))}

            {!pageLoading && noBusinesses && !searchLoading && noMerchantAccess && (
              <div className="flex justify-center mt-[-2px] pt-0 mb-4">
                <Button variant="primary" onClick={onLogout} loading={loggingOut}>
                  <LogOut />
                  Log out
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

export default function BusinessSelectionPage() {
  const { isLoading, isRedirecting } = useUserSessionGuard()

  if (isLoading || isRedirecting) {
    return <CenteredLoader />
  }

  return (
    <Suspense fallback={<CenteredLoader />}>
      <BusinessSelectionPageContent />
    </Suspense>
  )
}
